import { KEYBOARD_KEYS } from "@netapp/bxp-design-system-react";
import _ from "lodash";
import { useCallback } from "react";

export const useCategoryMenuNavigation = (containerRef, categoryRef, categoryButtonsRef = null) => {
    const findNextFocusableElement = useCallback((currentElement) => {
        if (categoryButtonsRef?.current && _.isArray(categoryButtonsRef.current)) {
            const currentCategoryName = currentElement?.getAttribute('data-nav-category');
            const currentIndex = _.findIndex(categoryButtonsRef.current, ref => 
                ref?.current?.getAttribute('data-nav-category') === currentCategoryName
            );
            return categoryButtonsRef.current[currentIndex + 1]?.current || null;
        }
        
        // Fallback: collect category buttons from DOM but avoid querySelector when possible
        const allCategoryButtons = [];
        
        // First try to find all buttons with data-nav-category in the document
        if (document.querySelectorAll) {
            const categoryElements = document.querySelectorAll('button[data-nav-category], div[data-nav-category]');
            _.forEach(categoryElements, el => {
                if (el.tagName === 'DIV') {
                    // For divs with data-nav-category, find the closest focusable parent
                    const focusableParent = el.closest('a') || el.closest('button') || el;
                    if (focusableParent && (focusableParent.tabIndex >= 0 || 
                        focusableParent.tagName === 'A' || focusableParent.tagName === 'BUTTON')) {
                        allCategoryButtons.push(focusableParent);
                    }
                } else if (el.tabIndex >= 0 || el.tagName === 'A' || el.tagName === 'BUTTON') {
                    allCategoryButtons.push(el);
                }
            });
        }

        const currentIndex = _.findIndex(allCategoryButtons, button => {
            const currentCategoryName = currentElement?.getAttribute('data-nav-category') || 
                                      currentElement?.querySelector?.('[data-nav-category]')?.getAttribute('data-nav-category');
            const buttonCategoryName = button.getAttribute('data-nav-category') || 
                                     button.querySelector?.('[data-nav-category]')?.getAttribute('data-nav-category');
            return currentCategoryName === buttonCategoryName;
        });

        return allCategoryButtons[currentIndex + 1] || null;
    }, [categoryButtonsRef]);

    const handleKeyDown = useCallback((e, currentIndex) => {
        let serviceCards = [];
        
        if (containerRef.current) {
            const containerChildren = _.toArray(containerRef.current.children || []);
            serviceCards = _.filter(containerChildren, child => 
                child.hasAttribute && child.hasAttribute('data-service-card')
            );
            
            // Fallback to querySelector if no children found
            if (serviceCards.length === 0 && containerRef.current.querySelectorAll) {
                const serviceCardElements = containerRef.current.querySelectorAll('[data-service-card]');
                serviceCards = _.toArray(serviceCardElements);
            }
        }
        
        if (serviceCards.length === 0) return;

        const { key } = e;
        let nextIndex = currentIndex;

        switch (key) {
            case KEYBOARD_KEYS.ARROW_DOWN:
                nextIndex = _.min([currentIndex + 1, serviceCards.length - 1]);
                break;
            case KEYBOARD_KEYS.ARROW_UP:
                nextIndex = _.max([currentIndex - 1, 0]);
                break;
            case KEYBOARD_KEYS.ARROW_LEFT:
            case KEYBOARD_KEYS.ESCAPE:
                e.preventDefault();
                categoryRef?.current?.focus();
                return;
            case KEYBOARD_KEYS.TAB:
                if (e.shiftKey && currentIndex === 0) {
                    e.preventDefault();
                    categoryRef?.current?.focus();
                    return;
                } else if (!e.shiftKey && currentIndex === serviceCards.length - 1) {
                    e.preventDefault();
                    const nextElement = findNextFocusableElement(categoryRef.current);
                    if (nextElement) {
                        nextElement.focus();
                    } else {
                        categoryRef?.current?.focus();
                    }
                    return;
                }
                return;
            default:
                return;
        }

        if (nextIndex !== currentIndex) {
            const nextCard = serviceCards[nextIndex];
            if (nextCard) {
                let favoriteButton = null;
                
                if (nextCard.children) {
                    // Look for button in immediate children
                    favoriteButton = _.find(_.toArray(nextCard.children), child => 
                        child.tagName === 'BUTTON'
                    );
                }
                
                // Fallback to querySelector if needed
                if (!favoriteButton && nextCard.querySelector) {
                    favoriteButton = nextCard.querySelector('button');
                }
                
                favoriteButton?.focus();
            }
        }
    }, [containerRef, categoryRef, findNextFocusableElement]);

    return { handleKeyDown };
};
